import { useOptionalPlayer } from "../../player/context.js";
import { useLatestRef } from "../../utils/use-latest-ref.js";
import { useDestroy } from "../../utils/use-destroy.js";
import { useForceRender } from "../../utils/use-force-render.js";
import { createSlider, selectControls } from "@videojs/core/dom";
import { useSnapshot } from "@videojs/store/react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { applyStyles } from "@videojs/utils/dom";
//#region src/ui/hooks/use-slider.ts
/**
* Manages slider input lifecycle for React.
*
* Wraps `createSlider()` from `@videojs/core/dom` and subscribes to its input state via `useSnapshot`. Returns split
* props for the root (pointer events) and thumb (keyboard/focus) elements.
*/
function useSlider(options) {
	const optionsRef = useLatestRef(options);
	const requestControlsLock = useOptionalPlayer(selectControls)?.requestControlsLock;
	const releaseControlsLockRef = useRef(null);
	const releaseControlsLock = useCallback(() => {
		releaseControlsLockRef.current?.();
		releaseControlsLockRef.current = null;
	}, []);
	useEffect(() => releaseControlsLock, [releaseControlsLock]);
	const rootElementRef = useRef(null);
	const thumbElementRef = useRef(null);
	const forceRender = useForceRender();
	const [slider] = useState(() => {
		const stableOptions = {
			getElement: () => rootElementRef.current,
			getThumbElement: () => thumbElementRef.current,
			getOrientation: () => optionsRef.current.orientation ?? "horizontal",
			isDisabled: () => optionsRef.current.disabled ?? false,
			getPercent: () => optionsRef.current.getPercent(),
			getStepPercent: () => optionsRef.current.getStepPercent(),
			getLargeStepPercent: () => optionsRef.current.getLargeStepPercent(),
			changeThrottle: optionsRef.current.changeThrottle,
			adjustPercent: optionsRef.current.adjustPercent,
			onValueChange: (percent) => optionsRef.current.onValueChange?.(percent),
			onValueCommit: (percent) => optionsRef.current.onValueCommit?.(percent),
			onPressStart: () => {
				releaseControlsLockRef.current ??= requestControlsLock?.() ?? null;
				optionsRef.current.onPressStart?.();
			},
			onPressEnd: () => {
				releaseControlsLock();
				optionsRef.current.onPressEnd?.();
			},
			onDragStart: () => optionsRef.current.onDragStart?.(),
			onDragEnd: () => optionsRef.current.onDragEnd?.()
		};
		return createSlider(stableOptions);
	});
	useDestroy(slider);
	const interaction = useSnapshot(slider.input, ({ dragging, pointing, focused }) => ({
		dragging,
		pointing,
		focused
	}));
	const input = {
		...slider.input.current,
		...interaction
	};
	const state = options.computeState(input);
	useLayoutEffect(() => {
		if (state.thumbAlignment === "edge" && rootElementRef.current && thumbElementRef.current) forceRender();
	}, [state.thumbAlignment]);
	const cssVars = options.getCSSVars(slider.adjustForAlignment(state));
	const syncStyles = useCallback((element = rootElementRef.current) => {
		if (!element) return;
		const { dragging, pointing } = slider.input.current;
		if (!dragging && !pointing) return;
		const next = optionsRef.current.computeState(slider.input.current);
		applyStyles(element, optionsRef.current.getCSSVars(slider.adjustForAlignment(next)));
	}, [slider]);
	useLayoutEffect(() => {
		syncStyles();
		return slider.input.subscribe(syncStyles);
	}, [slider, syncStyles]);
	const rootRef = useCallback((element) => {
		rootElementRef.current = element;
		syncStyles(element);
	}, [syncStyles]);
	const thumbRef = useCallback((element) => {
		thumbElementRef.current = element;
	}, []);
	return {
		state,
		input: slider.input,
		cssVars,
		rootRef,
		thumbRef,
		rootProps: slider.rootProps,
		rootStyle: slider.rootStyle,
		thumbProps: slider.thumbProps
	};
}
//#endregion
export { useSlider };

//# sourceMappingURL=use-slider.js.map